import axios from 'axios';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useSWR, { KeyedMutator, SWRConfiguration, useSWRConfig } from 'swr';
import { Config, useConfig } from '../config';
import { ApiCategoryProductsLoop54, ApiProductLoop54, ApiRedirect } from '../types';

export { Res } from './api-resources';
import { isSafeDraftId } from '../utils/draftPreview';

export type PreloadedData = Record<string, unknown>;

export class NotFoundError extends Error {
	type: string;
	// stripped, because an error message travels: into logs, into Sentry, into an error boundary
	constructor(path: string) {
		super(`resource "${withoutQuery(path)}" not found`);
		this.type = 'not-found';
	}
}

/**
 * An upstream that was briefly unavailable, as opposed to one that answered.
 *
 * The distinction earns its keep because the cms is read straight off an R2 bucket, and R2
 * answers 429 when it is read faster than the account allows. Folding that into NotFoundError
 * made a live page render as 404 for as long as the squeeze lasted - which search engines are
 * entitled to believe.
 */
export class TransientError extends Error {
	type: string;
	status: number | 'network';
	url: string;
	constructor(path: string, status: number | 'network') {
		super(`resource "${withoutQuery(path)}" was temporarily unavailable (${status})`);
		this.type = 'transient';
		this.status = status;
		this.url = withoutQuery(path);
	}
}

export function isRedirect(data: unknown | ApiRedirect | undefined): data is ApiRedirect {
	return !!data && (data as ApiRedirect).newUrl !== undefined;
}

export function isNotFound(error: unknown | NotFoundError | undefined): error is NotFoundError {
	return !!error && (error as NotFoundError).type === 'not-found';
}

export function isTransient(error: unknown | TransientError | undefined): error is TransientError {
	return !!error && (error as TransientError).type === 'transient';
}

/**
 * One temporary failure, as something worth counting.
 *
 * Two shapes of the same trouble are worth telling apart. `gave-up` is the fetch layer running
 * out of retries, which the visitor sees as a 503 or a loader. `read-as-answer` is worse and
 * quieter: a caller that treats the missing data as a fact - no session, so no negotiated
 * prices; no checkout state, so an empty basket. Nothing looks broken, so nothing gets reported,
 * which is exactly why these need counting before they can be fixed.
 */
export type TemporaryFailure = {
	url: string;
	status: number | 'network';
	kind: 'gave-up' | 'read-as-answer';
	/** what the page is about to do without, when the caller can name it */
	resource?: string;
	/** the host that did not answer, so failures group by which upstream is struggling */
	upstream: string;
};

/** What callers supply; `upstream` is derived from the url rather than repeated at every site. */
export type TemporaryFailureInput = Omit<TemporaryFailure, 'upstream'>;

/*
 * Registered from the entry points rather than imported here, so that this module - which the
 * server-rendering bundle also loads - stays free of the Sentry sdk.
 */
let reporter: (failure: TemporaryFailure) => void = () => {};

/*
 * Deduplicated by the identity of the error, not by what it was about.
 *
 * The problem being solved is narrow: SWR hands every subscriber the same error instance, and
 * useSession alone is called from a dozen components, each with an effect watching it. That is
 * one failure noticed a dozen times. Two separate requests that both fail are two failures, and
 * on the long-lived rendering server collapsing those would turn an outage into a blink.
 *
 * A WeakSet also means nothing is retained: entries go when the error does, so a long outage
 * across thousands of urls cannot grow this without bound.
 */
const alreadyReported = new WeakSet<object>();

export function onTemporaryFailure(handler: (failure: TemporaryFailure) => void) {
	reporter = handler;
}

/**
 * The query string never leaves the app with a failure report.
 *
 * Several endpoints carry the customer's own words in it - `?email=`, `?company=`, `?q=` with a
 * street, a checkout `sid` - and a report about an upstream being slow is no reason to hand any
 * of that to telemetry. The path still names the endpoint, which is all the report is for.
 *
 * Cut at `&` as well as `?`, because Loop54's urls are built as `search&q=...` with no question
 * mark at all - which is exactly how the customer's search text slipped past a first version of
 * this that only looked for `?`.
 */
export function withoutQuery(url: string): string {
	const cut = [url.indexOf('?'), url.indexOf('&')].filter((i) => i !== -1);
	return cut.length === 0 ? url : url.slice(0, Math.min(...cut));
}

function upstreamOf(url: string): string {
	try {
		return new URL(url).host || 'unknown';
	} catch {
		return 'relative';
	}
}

/**
 * `cause` is the error object the caller is reacting to, when there is one. Passing it collapses
 * the many consumers of a single failure into one report; omitting it reports every call, which
 * is what the fetch layer wants, since each of its calls is a request that really did fail.
 */
export function reportTemporaryFailure(failure: TemporaryFailureInput, cause?: object) {
	if (cause) {
		if (alreadyReported.has(cause)) return;
		alreadyReported.add(cause);
	}

	try {
		reporter({ ...failure, url: withoutQuery(failure.url), upstream: upstreamOf(failure.url) });
	} catch {
		// reporting must never be the thing that breaks the page
	}
}

/**
 * For content a page is better off without than broken by: banners, cms blocks bolted onto a
 * category or brand page. The page has its own reason to exist, so an upstream hiccup should
 * cost it a block, not the whole render.
 *
 * Only for supplementary content. A page whose entire body is the fetched document must not
 * quietly render empty - there the error has to travel.
 */
export async function optionalResource(promise: Promise<PreloadedData>): Promise<PreloadedData> {
	try {
		return await promise;
	} catch {
		return {};
	}
}

/**
 * Interface for an Api Resource Object. They represent api endpoints and encapsulate:
 * - logic to transform permalinks or entity ID:s into URIs
 */
export interface ApiResource<T, TParams = Record<string, string>> {
	isPrivate: boolean;
	server: 'api' | 'cms';
	swrConfig: Partial<SWRConfiguration>;
	uri(params: TParams): string;
	fetch(): T;
}

/**
 * A resource map for the server's cache of shared resources, with how long the response may
 * still be kept. GET only: nothing shared across requests is fetched any other way.
 */
export async function fetchResourceMapWithFreshness<T, TParams>(
	config: Config,
	res: ApiResource<T, TParams>,
	params: TParams,
	api?: string
): Promise<Fetched<PreloadedData>> {
	const url = (api ?? config.api) + res.uri(params);
	const { data, freshForSeconds } = await fetchWithFreshness(res.isPrivate)<T>(url);
	return { data: { [url]: data }, freshForSeconds };
}

/**
 * Fetches an Api Resource and formats it for populating the preloaded cache in Server Side Rendering.
 */
export async function fetchResourceMap<T, TParams>(
	config: Config,
	res: ApiResource<T, TParams>,
	params: TParams,
	api?: string,
	payload?: Record<any, any>,
	headers?: Record<any, any>,
	method: 'GET' | 'POST' = 'GET'
): Promise<PreloadedData> {
	const url = api ? api + res.uri(params) : config.api + res.uri(params);
	if (method === 'POST') {
		return { [url]: await postFetcher(res ? res?.isPrivate : false, payload, headers)(url) };
	} else {
		return { [url]: await fetcher(res.isPrivate)(url) };
	}
}

/**
 * Statuses that say "not right now" rather than "not here". A network error - no response at
 * all - counts as status 0 and is treated the same way.
 *
 * The whole 5xx family counts, not a list of the familiar ones: 501 and 507 are just as much
 * the upstream failing rather than reporting the resource missing, and enumerating left them
 * rendering as 404.
 */
const TRANSIENT_STATUSES = new Set([408, 425, 429]);

const isTransientStatus = (status: number) => status === 0 || status >= 500 || TRANSIENT_STATUSES.has(status);

/**
 * Two retries, and deliberately short ones: this sits on the server-rendering path, so every
 * millisecond is a millisecond the visitor waits. Long enough to ride out a rate limit, short
 * enough that a genuinely broken upstream still fails quickly.
 */
const RETRY_DELAYS_MS = [120, 360];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** A response and how long it may still be kept - null when it said nothing about it. */
export type Fetched<T> = { data: T; freshForSeconds: number | null };

type Attempt<T> = ({ ok: true } & Fetched<T>) | { ok: false; status: number };

/**
 * The max-age of a Cache-Control header, or null when there is none to follow.
 *
 * Only max-age: this is read by the server's own cache of shared resources, which keeps a
 * document for as long as the upstream said it may be kept, and the cms and the api both say
 * so this way. no-store and no-cache are taken at their word and answer 0.
 */
export function maxAgeOf(cacheControl: unknown): number | null {
	if (typeof cacheControl !== 'string') return null;
	if (/(^|,)\s*no-(store|cache)\s*(,|$)/i.test(cacheControl)) return 0;
	const match = /(^|,)\s*max-age\s*=\s*"?(\d+)"?\s*(,|$)/i.exec(cacheControl);
	return match ? Number(match[2]) : null;
}

/**
 * How much of a response's max-age is left, given how long a cache on the way already held it.
 *
 * A CDN answers with the max-age the origin set and an Age saying how long ago that was, so a
 * document a second from expiry arrives looking as fresh as one just written. Taking the
 * max-age at face value would keep it for up to twice as long as the origin allowed.
 */
export function freshnessOf(headers: Record<string, unknown> | undefined): number | null {
	const maxAge = maxAgeOf(headers?.['cache-control']);
	if (maxAge === null) return null;
	const age = Number(headers?.['age']);
	return Number.isFinite(age) && age > 0 ? Math.max(0, maxAge - Math.floor(age)) : maxAge;
}

async function attemptGet<T>(url: string, withCredentials: boolean, noCache: boolean): Promise<Attempt<T>> {
	try {
		const { data, status, headers } = await axios.get(url, {
			validateStatus: (status) => status < 600,
			withCredentials,
			headers: noCache ? noCacheHeaders : undefined,
		});
		/*
		 * In the browser this can be an Error rather than a response. NetworkErrorHandling's
		 * interceptor returns - rather than rejects - when a failure carried no response, so a
		 * connection error arrives here fulfilled, with no status on it. Left unnormalised it
		 * matched neither 2xx nor 404 nor any transient status, and became a NotFoundError.
		 */
		if (typeof status !== 'number') return { ok: false, status: 0 };
		if (status >= 200 && status <= 300) {
			return { ok: true, data: data as T, freshForSeconds: freshnessOf(headers) };
		}
		return { ok: false, status };
	} catch {
		// axios only rejects when there was no response to validate at all
		return { ok: false, status: 0 };
	}
}

export const fetcher =
	(withCredentials: boolean, noCache = false) =>
	async <T>(url: string): Promise<T> =>
		(await fetchWithFreshness(withCredentials, noCache)<T>(url)).data;

/** The fetcher, keeping what the response said about how long it may still be kept. */
export const fetchWithFreshness =
	(withCredentials: boolean, noCache = false) =>
	async <T>(url: string): Promise<Fetched<T>> => {
		for (let attempt = 0; ; attempt++) {
			const result = await attemptGet<T>(url, withCredentials, noCache);
			if (result.ok) return { data: result.data, freshForSeconds: result.freshForSeconds };

			// 404 is an answer. Retrying it only makes a missing page slower to say so.
			if (result.status === 404) throw new NotFoundError(url);

			const transient = isTransientStatus(result.status);
			// Everything else keeps the behaviour it has always had, deliberately: widening what
			// counts as transient would change how unrelated statuses reach the visitor.
			if (!transient) throw new NotFoundError(url);

			if (attempt >= RETRY_DELAYS_MS.length) {
				const status = result.status === 0 ? 'network' : result.status;
				reportTemporaryFailure({ url, status, kind: 'gave-up' });
				throw new TransientError(url, status);
			}
			await wait(RETRY_DELAYS_MS[attempt]);
		}
	};

const noCacheHeaders = {
	'Cache-Control': 'no-cache, no-store, max-age=0',
};

const noCacheConfig = {
	revalidateOnMount: true,
	revalidateIfStale: true,
	dedupingInterval: 0,
};

// specific for Loop54
/** Loop54's answer to a query it cannot parse is a 400, and an empty result set is our answer to that. */
const emptyLoopResponse = (): ApiCategoryProductsLoop54 => ({
	results: { count: 0, facets: [], items: [] },
	limit: 0,
	offset: 0,
	total: 0,
});

export const postFetcher =
	(withCredentials: boolean, payload?: Record<any, any>, headers?: Record<any, any>) =>
	async <T>(url: string): Promise<T> => {
		const queryParamsPos = url.indexOf('&');
		const strippedUrl = queryParamsPos !== -1 ? url.substring(0, queryParamsPos) : url;

		/*
		 * The same treatment as the GET path, because it fails the same way. The category and
		 * brand pages load their products through here, so a rate-limited or briefly broken
		 * Loop54 used to render those pages as 404 - the exact confusion this file now avoids
		 * everywhere else. Note axios is left on its default validateStatus here, so anything
		 * outside 2xx arrives as a rejection rather than a status to inspect.
		 */
		for (let attempt = 0; ; attempt++) {
			let response;
			try {
				response = await axios.post(strippedUrl, { ...payload }, { withCredentials, headers });
			} catch (error: any) {
				if (isNotFound(error)) throw error;

				const status = error?.response?.status;
				if (status === 400) return emptyLoopResponse() as T;
				if (status === 404) throw new NotFoundError(url);

				const code = typeof status === 'number' ? status : 0;
				// unchanged for anything that is not temporary
				if (!isTransientStatus(code)) throw new NotFoundError(url);
				if (attempt >= RETRY_DELAYS_MS.length) {
					const reported = code === 0 ? 'network' : code;
					reportTemporaryFailure({ url: strippedUrl, status: reported, kind: 'gave-up' });
					throw new TransientError(url, reported);
				}
				await wait(RETRY_DELAYS_MS[attempt]);
				continue;
			}

			/*
			 * The browser's interceptor fulfils with an Error rather than rejecting when a
			 * failure carried no response, so a connection error arrives here dressed as a reply.
			 * Typed as a response, it is not always one.
			 */
			if (typeof (response as { status?: unknown })?.status !== 'number') {
				if (attempt >= RETRY_DELAYS_MS.length) {
					reportTemporaryFailure({ url: strippedUrl, status: 'network', kind: 'gave-up' });
					throw new TransientError(url, 'network');
				}
				await wait(RETRY_DELAYS_MS[attempt]);
				continue;
			}

			/*
			 * Past this point the transport worked, so nothing that goes wrong from here is the
			 * upstream's doing. Reading a malformed body used to land in the catch above with no
			 * status, which retried a body that could not improve and recorded a request that
			 * arrived perfectly well as a network outage.
			 */
			try {
				const { data, status } = response;
				if (data && data.results) {
					data.results.items = transformLoopResponseToApiProducts(data.results.items);
				}
				if (status >= 200 && status <= 300) return data as T;
				throw new NotFoundError(url);
			} catch (error) {
				if (isNotFound(error)) throw error;
				/*
				 * A reply we could not read is not a missing page. Manufacturing a 404 here would
				 * retire a live category url over a schema change at Loop54 - the same confusion
				 * this file exists to avoid - so it travels as the processing failure it is:
				 * no retry, nothing counted against the upstream, and no 404 for a crawler.
				 */
				throw error instanceof Error ? error : new Error(`could not read the reply from ${strippedUrl}`);
			}
		}
	};

type ProductAttribute = {
	name: string;
	type: string;
	values: Array<any>;
};

export type ProductLoop54Api = {
	type: string;
	id: string;
	attributes: Array<ProductAttribute>;
};

export function transformLoopResponseToApiProducts(input: ProductLoop54Api[]): ApiProductLoop54[] {
	return input.map((product) => {
		const transformed: { [key: string]: any } = {};
		transformed['ecoLabels'] = [];
		product.attributes.forEach((attribute) => {
			if (['boost', 'categoryPath', 'groupId', 'mainCategoryPath', 'returnable'].includes(attribute.name)) return;

			let value = attribute.values[0];
			switch (attribute.type) {
				case 'number':
					value = Number(value);
					break;
				case 'boolean':
					value = Boolean(value);
					if (value === 'true') value = true;
					else if (value === 'false') value = false;
					break;
				case 'string':
					if (['minQuantity', 'bundleSize'].includes(attribute.name)) {
						value = Number(value);
					} else {
						value = String(value);
					}
			}

			if (attribute.name === 'imageUrl') attribute.name = 'image';

			if (attribute.name === 'ecoLabels') {
				value = attribute.values;
			}

			transformed[attribute.name] = value;
		});

		return transformed as ApiProductLoop54;
	});
}

export function useCacheInvalidator() {
	const { api } = useConfig();
	const { mutate } = useSWRConfig();
	return <T>(res: ApiResource<unknown, T>, params: T) => {
		mutate(api + res.uri(params));
	};
}

export function useCacheWildcardInvalidator() {
	const { api } = useConfig();
	const { mutate } = useSWRConfig();
	return (prefix: string) => {
		mutate((key) => typeof key === 'string' && key.startsWith(api + prefix));
	};
}

type ApiResponse<T> = {
	data: T | undefined;
	error: Error | NotFoundError | undefined;
	mutate: KeyedMutator<T>;
	isLoading: boolean;
};
export function useApi<T, TParams = {}>(
	res: ApiResource<T, TParams> | null,
	params: TParams,
	api?: string,
	payload?: Record<any, any>,
	headers?: Record<any, any>,
	method: 'POST' | 'GET' = 'GET'
): ApiResponse<T> {
	const config = useConfig();
	const resourceUri = res?.uri(params) ?? null;
	const endpoint = res ? api ?? config[res.server] : null;
	const isPrivate = res?.isPrivate ?? false;
	const key = endpoint && resourceUri ? endpoint + resourceUri : null;
	const swrFetcher = method === 'POST' ? postFetcher(isPrivate, payload, headers) : fetcher(isPrivate);
	const { data, error, mutate, isValidating } = useSWR<T>(key, swrFetcher, res?.swrConfig);

	return {
		data,
		error,
		mutate,
		isLoading: isValidating && undefined === data,
	};
}

export function useCms<T, TParams = {}>(res: ApiResource<T, TParams> | null, params: TParams): ApiResponse<T> {
	const config = useConfig();
	const location = useLocation();
	/*
	 * Read straight off the url rather than through useFilterQueryParams, which answers what a
	 * listing should filter by. These two are neither: they say which draft to render, and asking
	 * the filter parser for them is what let them reach Loop54 as facets.
	 */
	const draft = new URLSearchParams(location.search);
	const draftId = draft.get('draftId');
	const draftFile = draft.get('draftFile');
	const resourceUri = res?.uri(params) ?? null;
	const endpoint = res ? config.cmsContent : null;
	const isPrivate = false;
	const shouldFetchDraftFromCms = Boolean(draftId && isSafeDraftId(draftId) && draftFile === resourceUri);
	const draftCacheBuster = useMemo(
		() => (shouldFetchDraftFromCms ? Date.now().toString() : null),
		[shouldFetchDraftFromCms, draftId, draftFile, resourceUri]
	);

	let key = endpoint && resourceUri ? endpoint + resourceUri : null;
	if (shouldFetchDraftFromCms && draftCacheBuster) {
		const draftUrl = `${config.cmsContent}drafts/${draftId}/${resourceUri}`;
		const querySeparator = draftUrl.includes('?') ? '&' : '?';
		key = `${draftUrl}${querySeparator}cacheBust=${draftCacheBuster}`;
	}

	const swrFetcher = fetcher(isPrivate, shouldFetchDraftFromCms);

	const swrConfig: Partial<SWRConfiguration> = {
		...res?.swrConfig,
		...(shouldFetchDraftFromCms ? noCacheConfig : {}),
	};

	const { data, error, mutate, isValidating } = useSWR<T>(key, swrFetcher, swrConfig);

	return {
		data,
		error,
		mutate,
		isLoading: isValidating && undefined === data,
	};
}
